import api from '../../api/apiClient';

/**
 * Accounts / payments surface (`AccountController`).
 *
 * Credits are money coming in against jobs; debits are money going out, split
 * into salary, billings and reimbursements by the `/debits/*` routes.
 */

const unwrap = async <T>(promise: Promise<{ data: T }>): Promise<T> => (await promise).data;

/** Rows returned by the debit listings — shapes vary slightly per debit type. */
export interface DebitRecord {
  id?: number;
  employee_id?: number;
  employee_name?: string;
  amount?: number | string;
  particulars?: string;
  payment_method?: string;
  payment_date?: string;
  status?: string;
  [key: string]: unknown;
}

export interface AccountsJob {
  id?: number;
  job_code?: string;
  job_name?: string;
  client_name?: string;
  job_value?: number | string;
  amount_credited?: number | string;
  amount_requested?: number | string;
  balance?: number | string;
  [key: string]: unknown;
}

export type DebitType = 'salary' | 'billings' | 'reimbursements';

const toArray = <T>(body: any): T[] => {
  if (Array.isArray(body)) return body;
  if (Array.isArray(body?.data)) return body.data;
  return [];
};

/* ── Credits ─────────────────────────────────────────────────────────────── */

export async function getAccountsJobs(): Promise<AccountsJob[]> {
  return toArray<AccountsJob>(await unwrap(api.get('/api/accounts/jobs')));
}

export async function getAccountsJob(jobId: number | string): Promise<any> {
  return unwrap(api.get(`/api/accounts/job/${jobId}`));
}

export async function createPaymentRequest(
  jobId: number | string,
  payload: Record<string, unknown>
): Promise<any> {
  return unwrap(api.post(`/api/accounts/job/${jobId}/payment-request`, payload));
}

export async function updateProforma(
  jobId: number | string,
  payload: Record<string, unknown>
): Promise<any> {
  return unwrap(api.put(`/api/accounts/job/${jobId}/proforma`, payload));
}

export async function updateCreditNote(
  jobId: number | string,
  payload: Record<string, unknown>
): Promise<any> {
  return unwrap(api.put(`/api/accounts/job/${jobId}/credit-note`, payload));
}

export async function updateInvoice(
  jobId: number | string,
  payload: Record<string, unknown>
): Promise<any> {
  return unwrap(api.put(`/api/accounts/job/${jobId}/invoice`, payload));
}

export async function updateJobFromAccounts(
  jobId: number | string,
  payload: Record<string, unknown>
): Promise<any> {
  return unwrap(api.put(`/api/accounts/job/${jobId}/update`, payload));
}

/* ── Debits ──────────────────────────────────────────────────────────────── */

const DEBIT_PATHS: Record<DebitType, string> = {
  salary: '/api/accounts/debits/salary',
  billings: '/api/accounts/debits/billings',
  reimbursements: '/api/accounts/debits/reimbursements',
};

export async function getDebits(type: DebitType): Promise<DebitRecord[]> {
  return toArray<DebitRecord>(await unwrap(api.get(DEBIT_PATHS[type])));
}

/** `PUT /api/accounts/debits/{salaryRecordId}` — marks a salary debit paid. */
export async function updateSalaryDebit(
  salaryRecordId: number | string,
  payload: { status: string; payment_proof?: number }
): Promise<any> {
  return unwrap(api.put(`/api/accounts/debits/${salaryRecordId}`, payload));
}

export interface CreateDebitPayload {
  debit_type: DebitType;
  amount: string | number;
  particulars?: string;
  payment_method?: string;
  payment_date?: string;
  bill_proof?: number;
  receipt_proof?: number;
}

export async function createDebit(payload: CreateDebitPayload): Promise<any> {
  return unwrap(api.post('/api/accounts/create-debit', payload));
}
